<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import ConversationFunnelMappingsAPI from 'dashboard/api/conversationFunnelMappings';
import Spinner from 'shared/components/Spinner.vue';
import ActivityItem from './ActivityItem.vue';
import AddActivityNote from './AddActivityNote.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
  mappingId: {
    type: [Number, String],
    required: true,
  },
  showAddNote: {
    type: Boolean,
    default: true,
  },
  maxItems: {
    type: Number,
    default: 20,
  },
});

const { t } = useI18n();

const activities = ref([]);
const isLoading = ref(false);
const isAddingNote = ref(false);
const currentPage = ref(1);
const totalPages = ref(1);
const hasMore = computed(() => currentPage.value < totalPages.value);

const sortedActivities = computed(() => {
  return [...activities.value].sort(
    (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
  );
});

async function fetchActivities(page = 1, append = false) {
  isLoading.value = true;
  try {
    const { data } = await ConversationFunnelMappingsAPI.getActivities(
      props.conversationId,
      props.mappingId,
      { page, per_page: props.maxItems }
    );

    if (append) {
      activities.value = [...activities.value, ...data.activities];
    } else {
      activities.value = data.activities;
    }

    currentPage.value = data.meta.current_page;
    totalPages.value = data.meta.total_pages;
  } catch {
    // Activity fetch failed
  } finally {
    isLoading.value = false;
  }
}

async function handleAddNote(content) {
  isAddingNote.value = true;
  try {
    await ConversationFunnelMappingsAPI.addNote(
      props.conversationId,
      props.mappingId,
      content
    );
    // Refresh activities
    await fetchActivities(1, false);
  } catch {
    // Note add failed
  } finally {
    isAddingNote.value = false;
  }
}

function loadMore() {
  if (hasMore.value && !isLoading.value) {
    fetchActivities(currentPage.value + 1, true);
  }
}

onMounted(() => {
  fetchActivities();
});

watch(
  () => [props.conversationId, props.mappingId],
  () => {
    activities.value = [];
    currentPage.value = 1;
    fetchActivities();
  }
);
</script>

<template>
  <div class="activity-timeline">
    <div class="activity-timeline__header">
      <h3 class="activity-timeline__title">
        <fluent-icon icon="arrow-clockwise" size="16" />
        {{ t('CRM.ACTIVITY.TIMELINE_TITLE') }}
      </h3>
      <span class="activity-timeline__count">
        {{ activities.length }} {{ t('CRM.ACTIVITY.ACTIVITIES') }}
      </span>
    </div>

    <!-- Add Note -->
    <AddActivityNote
      v-if="showAddNote"
      :is-loading="isAddingNote"
      @add="handleAddNote"
    />

    <!-- Loading state -->
    <div
      v-if="isLoading && activities.length === 0"
      class="activity-timeline__loading"
    >
      <Spinner size="small" />
      <span>{{ t('CRM.ACTIVITY.LOADING') }}</span>
    </div>

    <!-- Empty state -->
    <div
      v-else-if="!isLoading && activities.length === 0"
      class="activity-timeline__empty"
    >
      <fluent-icon icon="arrow-clockwise" size="32" />
      <p>{{ t('CRM.ACTIVITY.NO_ACTIVITIES') }}</p>
    </div>

    <!-- Activities list -->
    <div v-else class="activity-timeline__list">
      <ActivityItem
        v-for="activity in sortedActivities"
        :key="activity.id"
        :activity="activity"
      />

      <!-- Load more button -->
      <button
        v-if="hasMore"
        class="activity-timeline__load-more"
        :disabled="isLoading"
        @click="loadMore"
      >
        <span v-if="isLoading">{{ t('CRM.ACTIVITY.LOADING_MORE') }}</span>
        <span v-else>{{ t('CRM.ACTIVITY.LOAD_MORE') }}</span>
      </button>
    </div>
  </div>
</template>

<style scoped>
.activity-timeline {
  @apply flex flex-col;
}

.activity-timeline__header {
  @apply flex items-center justify-between mb-3 pb-2 border-b border-slate-200 dark:border-slate-700;
}

.activity-timeline__title {
  @apply flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-200;
}

.activity-timeline__count {
  @apply text-xs text-slate-500 dark:text-slate-400;
}

.activity-timeline__loading,
.activity-timeline__empty {
  @apply flex flex-col items-center justify-center py-8 text-slate-500 dark:text-slate-400;
}

.activity-timeline__empty {
  @apply gap-2;
}

.activity-timeline__list {
  @apply space-y-2;
}

.activity-timeline__load-more {
  @apply w-full py-2 text-sm text-woot-500 hover:text-woot-600 hover:bg-woot-50 dark:hover:bg-woot-900/20 rounded-md transition-colors;
}
</style>
